<template>
  <AuthLayout title="Register for an account">
    <form @submit.prevent="onSubmit" class="space-y-6" novalidate>
      <FormErrorSummary :errors="errorSummary" />

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label for="first_name" class="block text-sm font-medium text-gray-700">
            First Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <div class="mt-1">
            <input
              v-model="firstName"
              id="first_name"
              type="text"
              autocomplete="given-name"
              class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="firstNameDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="touch('firstName')"
            />
          </div>
          <p v-if="firstNameDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ firstNameDisplayError }}</p>
        </div>

        <div>
          <label for="last_name" class="block text-sm font-medium text-gray-700">
            Last Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <div class="mt-1">
            <input
              v-model="lastName"
              id="last_name"
              type="text"
              autocomplete="family-name"
              class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="lastNameDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="touch('lastName')"
            />
          </div>
          <p v-if="lastNameDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ lastNameDisplayError }}</p>
        </div>
      </div>

      <div>
        <label for="email" class="block text-sm font-medium text-gray-700">
          Email address <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <div class="mt-1">
          <input
            v-model="email"
            id="email"
            type="email"
            autocomplete="email"
            class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
            :class="emailDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('email')"
          />
        </div>
        <p v-if="emailDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ emailDisplayError }}</p>
      </div>

      <div>
        <label for="password" class="block text-sm font-medium text-gray-700">
          Password <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <div class="mt-1">
          <input
            v-model="password"
            id="password"
            type="password"
            autocomplete="new-password"
            class="appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
            :class="passwordDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('password')"
          />
        </div>

        <!-- Password strength -->
        <div v-if="password" class="mt-2">
          <div class="flex items-center gap-2">
            <div class="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                class="h-1.5 rounded-full transition-all duration-300"
                :class="passwordStrengthClass"
                :style="{ width: passwordStrength + '%' }"
              ></div>
            </div>
            <span class="text-xs font-medium" :class="passwordStrengthTextClass">{{ passwordStrengthLabel }}</span>
          </div>
        </div>

        <p v-if="passwordDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ passwordDisplayError }}</p>
        <p class="mt-1.5 text-xs text-gray-500">At least 8 characters with uppercase, lowercase, and a number</p>
      </div>

      <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
        {{ error }}
      </div>

      <button type="submit" class="btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium">
        Register
      </button>
    </form>

    <template #footer-links>
      <div class="text-sm">
        <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">
          Already have an account? Login
        </router-link>
      </div>
    </template>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useFormValidation } from '../../composables/useFormValidation'
import AuthLayout from '../../layouts/AuthLayout.vue'
import FormErrorSummary from '../FormErrorSummary.vue'
import { vm } from '../../utils/validationMessages'

const authStore = useAuthStore()
const router = useRouter()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')

const error = computed(() => authStore.error)

const { submitAttempted, touch, displayError, attemptSubmit } = useFormValidation()

// Field-level validation rules
const firstNameErr = computed(() => {
  if (!firstName.value.trim()) return vm.firstName.required
  return ''
})

const lastNameErr = computed(() => {
  if (!lastName.value.trim()) return vm.lastName.required
  return ''
})

const emailErr = computed(() => {
  if (!email.value.trim()) return vm.email.required
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) return vm.email.invalid
  return ''
})

const passwordErr = computed(() => {
  if (!password.value) return vm.password.required
  if (password.value.length < 8) return vm.password.minLength
  if (!/[A-Z]/.test(password.value)) return vm.password.uppercase
  if (!/[a-z]/.test(password.value)) return vm.password.lowercase
  if (!/[0-9]/.test(password.value)) return vm.password.number
  return ''
})

// Displayed errors (only shown after touch or submit attempt)
const firstNameDisplayError = computed(() => displayError('firstName', firstNameErr.value))
const lastNameDisplayError = computed(() => displayError('lastName', lastNameErr.value))
const emailDisplayError = computed(() => displayError('email', emailErr.value))
const passwordDisplayError = computed(() => displayError('password', passwordErr.value))

// Password strength indicator
const passwordStrength = computed(() => {
  const pwd = password.value
  if (!pwd) return 0
  let s = 0
  if (pwd.length >= 8) s += 25
  if (pwd.length >= 12) s += 10
  if (/[A-Z]/.test(pwd)) s += 20
  if (/[a-z]/.test(pwd)) s += 20
  if (/[0-9]/.test(pwd)) s += 20
  if (/[^A-Za-z0-9]/.test(pwd)) s += 15
  return Math.min(s, 100)
})

const passwordStrengthClass = computed(() => {
  if (passwordStrength.value < 40) return 'bg-red-500'
  if (passwordStrength.value < 60) return 'bg-yellow-500'
  if (passwordStrength.value < 80) return 'bg-blue-500'
  return 'bg-green-500'
})

const passwordStrengthTextClass = computed(() => {
  if (passwordStrength.value < 40) return 'text-red-600'
  if (passwordStrength.value < 60) return 'text-yellow-600'
  if (passwordStrength.value < 80) return 'text-blue-600'
  return 'text-green-600'
})

const passwordStrengthLabel = computed(() => {
  if (passwordStrength.value < 40) return 'Weak'
  if (passwordStrength.value < 60) return 'Fair'
  if (passwordStrength.value < 80) return 'Good'
  return 'Strong'
})

// Summary shown at top after first submit attempt
const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (firstNameErr.value) items.push({ field: 'firstName', label: 'First Name', message: firstNameErr.value })
  if (lastNameErr.value) items.push({ field: 'lastName', label: 'Last Name', message: lastNameErr.value })
  if (emailErr.value) items.push({ field: 'email', label: 'Email address', message: emailErr.value })
  if (passwordErr.value) items.push({ field: 'password', label: 'Password', message: passwordErr.value })
  return items
})

const onSubmit = async () => {
  if (!attemptSubmit({
    firstName: firstNameErr.value,
    lastName: lastNameErr.value,
    email: emailErr.value,
    password: passwordErr.value,
  })) return

  try {
    await authStore.register({
      email: email.value,
      password: password.value,
      first_name: firstName.value,
      last_name: lastName.value,
    })
    router.push('/login')
  } catch (err) {
    console.error('Registration failed:', err)
  }
}
</script>
